import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import { BlogEditor } from "./blog/BlogEditor";
import { CATEGORIES, IMAGES, slugify, sectionKey, emptySection } from "./blog/blogCmsUtils";

export const BlogCMSPanel = () => {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setIsNew(true);
    setEditing({ slug: "", title: "", category: CATEGORIES[0], excerpt: "", image: IMAGES[0], reading_time: 4, status: "published", sections: [emptySection()], takeawaysText: "" });
  };

  const startEdit = (p) => {
    setIsNew(false);
    setEditing({
      ...p,
      sections: p.sections.map((s) => ({ _key: sectionKey(), heading: s.heading, paragraphsText: s.paragraphs.join("\n\n"), bulletsText: (s.bullets || []).join("\n") })),
      takeawaysText: p.takeaways.join("\n"),
    });
  };

  const save = async () => {
    const body = {
      slug: editing.slug || slugify(editing.title),
      title: editing.title,
      category: editing.category,
      excerpt: editing.excerpt,
      image: editing.image,
      reading_time: Number(editing.reading_time) || 4,
      published: editing.published || null,
      author: editing.author || "Europcar UAE Editorial",
      status: editing.status,
      sections: editing.sections
        .filter((s) => s.heading.trim())
        .map((s) => ({
          heading: s.heading.trim(),
          paragraphs: s.paragraphsText.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
          bullets: s.bulletsText.trim() ? s.bulletsText.split("\n").map((b) => b.trim()).filter(Boolean) : null,
        })),
      takeaways: editing.takeawaysText.split("\n").map((t) => t.trim()).filter(Boolean),
    };
    setBusy(true);
    try {
      if (isNew) await api.post("/admin/blog", body);
      else await api.put(`/admin/blog/${editing.slug}`, body);
      toast.success(isNew ? "Article published" : "Article updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete article "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/${slug}`);
      toast.success("Article deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (posts === null)
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#009900]" /></div>;

  if (editing)
    return <BlogEditor editing={editing} setEditing={setEditing} isNew={isNew} busy={busy} onSave={save} onBack={() => setEditing(null)} />;

  return (
    <div data-testid="blog-cms-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <p className="text-sm text-[#57685e]">{posts.length} article{posts.length !== 1 ? "s" : ""} · competitor mentions are blocked automatically</p>
        <button onClick={startNew} className="btn-cta rounded px-5 py-2.5 text-xs flex items-center gap-2" data-testid="blog-new-article-btn">
          <Plus className="w-4 h-4" /> New article
        </button>
      </div>
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.slug} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex flex-wrap items-center gap-4" data-testid={`blog-cms-row-${p.slug}`}>
            <img src={p.image} alt={p.title} className="w-20 h-14 object-cover rounded-lg" />
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sm text-[#0F1E17]">{p.title}</span>
                <span className={`text-[10px] uppercase tracking-wide font-semibold rounded-full px-2 py-0.5 border ${p.status === "published" ? "bg-[#f0f9f0] text-[#009900] border-[#009900]/30" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                  {p.status}
                </span>
              </div>
              <div className="text-xs text-[#57685e] mt-0.5">{p.category} · {p.reading_time} min · {dayjs(p.published).format("DD MMM YYYY")} · /blog/{p.slug}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(p)} className="border-2 border-[#009900] text-[#009900] rounded px-3 py-2 text-xs font-medium uppercase tracking-wide hover:bg-[#f0f9f0] flex items-center gap-1.5" data-testid="blog-edit-btn">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button onClick={() => remove(p.slug)} className="border-2 border-red-500 text-red-600 rounded px-3 py-2 text-xs font-medium uppercase tracking-wide hover:bg-red-50 flex items-center gap-1.5" data-testid="blog-delete-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
